interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

function buildPageList(page: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | '...')[] = [1];

  if (page > 3) pages.push('...');

  const start = Math.max(2, page - 1);
  const end = Math.min(total - 1, page + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  if (page < total - 2) pages.push('...');

  pages.push(total);
  return pages;
}

export function Pagination({ page, totalPages, onChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = buildPageList(page, totalPages);

  return (
    <div className="flex items-center justify-center gap-1 mt-8">
      <div className="flex sm:hidden items-center justify-center gap-2 w-full">
        <button
          onClick={() => onChange(1)}
          disabled={page === 1}
          aria-label="Primeira página"
          className="w-11 h-11 flex items-center justify-center rounded-lg border border-champagne-dark text-text-medium disabled:opacity-40 hover:bg-champagne hover:border-chocolate transition-colors"
        >
          «
        </button>

        <button
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
          aria-label="Página anterior"
          className="w-11 h-11 flex items-center justify-center rounded-lg border border-champagne-dark text-text-medium disabled:opacity-40 hover:bg-champagne hover:border-chocolate transition-colors"
        >
          ‹
        </button>

        <span className="text-sm font-medium text-text-medium">
          Página {page} de {totalPages}
        </span>

        <button
          onClick={() => onChange(page + 1)}
          disabled={page === totalPages}
          aria-label="Próxima página"
          className="w-11 h-11 flex items-center justify-center rounded-lg border border-champagne-dark text-text-medium disabled:opacity-40 hover:bg-champagne hover:border-chocolate transition-colors"
        >
          ›
        </button>

        <button
          onClick={() => onChange(totalPages)}
          disabled={page === totalPages}
          aria-label="Última página"
          className="w-11 h-11 flex items-center justify-center rounded-lg border border-champagne-dark text-text-medium disabled:opacity-40 hover:bg-champagne hover:border-chocolate transition-colors"
        >
          »
        </button>
      </div>

      <div className="hidden sm:flex items-center justify-center gap-1">
        <button
          onClick={() => onChange(1)}
          disabled={page === 1}
          aria-label="Primeira página"
          className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-lg border border-champagne-dark text-text-medium disabled:opacity-40 hover:bg-champagne hover:border-chocolate transition-colors"
        >
          «
        </button>

        <button
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
          aria-label="Página anterior"
          className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-lg border border-champagne-dark text-text-medium disabled:opacity-40 hover:bg-champagne hover:border-chocolate transition-colors"
        >
          ‹
        </button>

        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`ellipsis-${i}`} className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center text-text-light text-sm">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              className={[
                'w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-lg text-sm font-medium transition-colors',
                p === page
                  ? 'bg-chocolate text-white border border-chocolate'
                  : 'border border-champagne-dark text-text-medium hover:bg-champagne hover:border-chocolate',
              ].join(' ')}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={() => onChange(page + 1)}
          disabled={page === totalPages}
          aria-label="Próxima página"
          className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-lg border border-champagne-dark text-text-medium disabled:opacity-40 hover:bg-champagne hover:border-chocolate transition-colors"
        >
          ›
        </button>

        <button
          onClick={() => onChange(totalPages)}
          disabled={page === totalPages}
          aria-label="Última página"
          className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-lg border border-champagne-dark text-text-medium disabled:opacity-40 hover:bg-champagne hover:border-chocolate transition-colors"
        >
          »
        </button>
      </div>
    </div>
  );
}
